import React, { useState, useEffect } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Card from "@mui/material/Card";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import { useHistory } from "react-router-dom";
import { useAuthz } from "context/AuthzContext";
import { useClient } from "context/ClientContext";
import { listClientProfiles, apiRequest } from "services/tenantUserAdminService";
import { TC } from "../tokens/text";

const CARD_BG = "linear-gradient(127.09deg, rgba(6,11,40,0.94) 19.41%, rgba(10,14,35,0.49) 76.65%)";

const STATUS_COLOURS = {
  draft:     "#F7B731",
  approved:  "#01B574",
  submitted: "#4E72CE",
  filed:     "#A29BFE",
};

function StatusChip({ status }) {
  const colour = STATUS_COLOURS[status] || "#aaa";
  const label  = status ? status.charAt(0).toUpperCase() + status.slice(1) : "No draft";
  return (
    <span style={{ background: `${colour}18`, color: colour, border: `1px solid ${colour}44`, borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>
      {label}
    </span>
  );
}

function VATPeriodsPage() {
  const { tenantId } = useAuthz();
  const history = useHistory();
  const { pinnedClientId, pinClient } = useClient() || {};

  const [profiles, setProfiles]           = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [drafts, setDrafts]               = useState([]);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState("");

  useEffect(() => {
    if (!tenantId) return;
    listClientProfiles({ tenantId, status: "active" })
      .then(d => {
        const list = d.clientProfiles || [];
        setProfiles(list);
        const nextSelected =
          (pinnedClientId && list.some((client) => client.id === pinnedClientId) && pinnedClientId) ||
          list[0]?.id ||
          "";
        setSelectedClient(nextSelected);
        if (nextSelected && nextSelected !== pinnedClientId) {
          const match = list.find((client) => client.id === nextSelected);
          pinClient?.(nextSelected, match?.legalName || match?.tradingName || nextSelected);
        }
      }).catch(() => {});
  }, [tenantId, pinnedClientId, pinClient]);

  useEffect(() => {
    if (!tenantId || !selectedClient) return;
    setLoading(true); setError("");
    apiRequest(`/tenant/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(selectedClient)}/vat-drafts`)
      .then(d => setDrafts(d.drafts || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [tenantId, selectedClient]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <VuiBox mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <VuiBox>
            <VuiTypography variant="h5" fontWeight="bold" sx={{ color: TC.primary }}>📋 VAT Periods</VuiTypography>
            <VuiTypography sx={{ color: TC.secondary, fontSize: 13 }}>
              All VAT return drafts for the selected client
            </VuiTypography>
          </VuiBox>
          <Select aria-label="Select client" value={selectedClient} onChange={e => {
            const next = e.target.value;
            setSelectedClient(next);
            const match = profiles.find((client) => client.id === next);
            pinClient?.(next, match?.legalName || match?.tradingName || next);
          }}
            sx={{ color: TC.primary, background: "#f4f8ff", border: "1px solid #d8e6f7", borderRadius: 2, minWidth: 220, "& .MuiSvgIcon-root": { color: TC.primary }, fontSize: 13 }}>
            {profiles.map(p => <MenuItem key={p.id} value={p.id}>{p.legalName || p.tradingName || p.id}</MenuItem>)}
          </Select>
        </VuiBox>

        {error && (
          <VuiBox mb={2} sx={{ background: "rgba(255,71,87,0.1)", border: "1px solid rgba(255,71,87,0.3)", borderRadius: 2, p: "12px 16px" }}>
            <VuiTypography sx={{ color: "#f44336", fontSize: 13 }}>{error}</VuiTypography>
          </VuiBox>
        )}

        <Card sx={{ background: CARD_BG, border: "1px solid #d8e6f7", borderRadius: "16px" }}>
          <VuiBox p={3}>
            {loading ? (
              <VuiTypography sx={{ color: TC.secondary, fontSize: 13, textAlign: "center", py: 4 }}>Loading…</VuiTypography>
            ) : drafts.length === 0 ? (
              <VuiBox textAlign="center" py={4}>
                <VuiTypography sx={{ color: TC.secondary, fontSize: 14, mb: 2 }}>
                  No VAT drafts yet for this client.
                </VuiTypography>
                <VuiButton size="small" color="info" variant="outlined" onClick={() => history.push("/vat-return")} sx={{ fontSize: 12 }}>
                  Go to VAT Return →
                </VuiButton>
              </VuiBox>
            ) : (
              <>
                {/* Header row */}
                <VuiBox display="flex" sx={{ borderBottom: "1px solid #e4eef8", pb: 1, mb: 1 }}>
                  {["Period", "Status", "Net VAT (Box 5)", "Generated", "Approved", ""].map(h => (
                    <VuiTypography key={h} sx={{ color: TC.secondary, fontSize: 11, fontWeight: 700, textTransform: "uppercase", flex: h === "" ? "none" : 1, minWidth: h === "" ? 80 : undefined }}>{h}</VuiTypography>
                  ))}
                </VuiBox>

                {drafts.map(d => (
                  <VuiBox key={d.id} display="flex" alignItems="center"
                    sx={{ borderBottom: "1px solid #e4eef8", py: "10px", "&:last-child": { borderBottom: "none" } }}>
                    <VuiTypography sx={{ color: TC.primary, fontSize: 13, fontWeight: 700, flex: 1 }}>{d.period_id}</VuiTypography>
                    <VuiBox flex={1}><StatusChip status={d.status} /></VuiBox>
                    <VuiTypography sx={{ color: (d.boxes?.box5 || 0) < 0 ? "#01B574" : "#F7B731", fontSize: 13, fontWeight: 700, flex: 1 }}>
                      {new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(d.boxes?.box5 || 0)}
                    </VuiTypography>
                    <VuiTypography sx={{ color: TC.secondary, fontSize: 12, flex: 1 }}>
                      {d.generated_at ? new Date(d.generated_at).toLocaleDateString("en-GB") : "—"}
                    </VuiTypography>
                    <VuiTypography sx={{ color: TC.secondary, fontSize: 12, flex: 1 }}>
                      {d.approved_at ? new Date(d.approved_at).toLocaleDateString("en-GB") : "—"}
                    </VuiTypography>
                    <VuiButton size="small" color="info" variant="outlined" sx={{ fontSize: 11, minWidth: 80 }}
                      onClick={() => history.push("/vat-return")}>
                      Open →
                    </VuiButton>
                  </VuiBox>
                ))}
              </>
            )}
          </VuiBox>
        </Card>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default VATPeriodsPage;

